import type { CreateDraftOrderResponse, DraftOrderResult } from "@/types";
import type { DiscountDetail } from "@/lib/discount-store";

const HANDOFF_PREFIX = "express_draft_discounts:";

type DraftResponse = CreateDraftOrderResponse | DraftOrderResult[];

function marketplaceDrafts(response: DraftResponse): DraftOrderResult[] {
  if (Array.isArray(response)) return response;
  return response.draftOrders ?? [];
}

export function draftOrderFailed(response: DraftResponse): boolean {
  const value = response as DraftResponse & { success?: boolean };
  return value.success === false || marketplaceDrafts(response).some((draft) => !draft.success);
}

export function automaticDiscountsFromDraft(response: DraftResponse): DiscountDetail[] {
  if (draftOrderFailed(response)) return [];

  const marketplace = marketplaceDrafts(response);
  if (marketplace.length > 0) {
    return marketplace.flatMap((draft) => {
      const amount = Number.parseFloat(draft.discounts?.total_discounts?.amount ?? "0");
      if (!Number.isFinite(amount) || amount <= 0) return [];
      return [{
        code: "Automated Discount",
        brandId: draft.brand_id,
        discountValue: 0,
        totalValue: Math.round(amount * 100),
        discountsOn: [],
        type: "AutomatedDiscount",
        isShippingDiscount: false,
      }];
    });
  }

  const amount = Number.parseFloat(
    (response as CreateDraftOrderResponse).discounts?.total_discounts?.amount ?? "0",
  );
  if (!Number.isFinite(amount) || amount <= 0) return [];
  return [{
    code: "Automated Discount",
    discountValue: 0,
    totalValue: Math.round(amount * 100),
    discountsOn: [],
    type: "AutomatedDiscount",
    isShippingDiscount: false,
  }];
}

export function saveDraftDiscountHandoff(sessionId: string, discounts: DiscountDetail[]): void {
  try {
    const key = HANDOFF_PREFIX + sessionId;
    if (discounts.length === 0) {
      sessionStorage.removeItem(key);
    } else {
      sessionStorage.setItem(key, JSON.stringify(discounts));
    }
  } catch {
    // Discount handoff must not block checkout if browser storage is unavailable.
  }
}

export function takeDraftDiscountHandoff(sessionId: string): DiscountDetail[] {
  const key = HANDOFF_PREFIX + sessionId;
  try {
    const raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    if (!raw) return [];
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? value as DiscountDetail[] : [];
  } catch {
    return [];
  }
}
